import { Button, Loader } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconExternalLink, IconShieldCheck } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { useClient, useOrpc } from '@/hooks/useClient'
import { useIdentityVerification } from '@/hooks/useIdentityVerification'
import { PersonaAttemptSummary } from '@/pages/Patient/components/PersonaAttemptSummary'
import { PersonaIdImage } from '@/pages/Patient/components/PersonaIdImage'
import {
  IDENTITY_STATUS_LABELS,
  STATUS_CHIP_CLASSES,
} from '@/pages/Patient/components/identityStatus'
import { buildPatientChartPath } from '@/pages/Patient/patientChartPath'
import { logError } from '@/utils/errorUtils'

/**
 * The identity follow-up task's own panel in the task drawer: the patient's identity status, what
 * Persona found and the ID it checked — the same as the chart's Identity Verification card — and
 * **Mark identity verified** beside them, so the coordinator decides with the evidence in view.
 * Verifying completes the task on the middleware side; the board then shows it done.
 */
export function IdentityTaskPanel({ patientId, boardId }: { patientId: string; boardId: string }) {
  const client = useClient()
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const { data: identity, loading, refresh } = useIdentityVerification(patientId)
  const [verifying, setVerifying] = useState(false)

  const verify = async () => {
    setVerifying(true)
    try {
      await client.markIdentityVerified({ id: patientId, verified: true })
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: orpc.boardGet.key({ input: { id: boardId } }) }),
        queryClient.invalidateQueries({ queryKey: orpc.patientCareTasks.key() }),
        queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patientId) }),
      ])
      showNotification({
        color: 'green',
        message: 'Identity verified. Full portal access unlocked.',
      })
    } catch (error) {
      logError('patient', 'Verify identity from the identity task', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to verify identity.',
      })
    } finally {
      setVerifying(false)
    }
  }

  const confirmVerify = () =>
    modals.openConfirmModal({
      title: 'Mark identity verified?',
      children:
        "This unlocks full patient portal access — medical records, lab results, messages, and care plans. Confirm you have verified this patient's government ID.",
      labels: { confirm: 'Mark verified', cancel: 'Cancel' },
      confirmProps: { color: 'green' },
      onConfirm: () => void verify(),
    })

  const attempt = identity?.latestAttempt
  // `canChange` is the middleware's "care coordinator or doctor" answer — the staff who see the
  // Persona detail, and the ones these tasks are for.
  const canVerify = !!identity && !identity.verified && identity.personaEnrollment.canChange

  return (
    <section
      aria-label="Identity verification"
      className="flex flex-col gap-3 rounded-lg border border-[#E2E8F0] p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconShieldCheck size={16} className="text-slate-500" aria-hidden />
          <p className="text-sm font-semibold text-slate-900">Identity verification</p>
          {identity ? (
            <span
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CHIP_CLASSES[identity.status]}`}
            >
              {IDENTITY_STATUS_LABELS[identity.status]}
            </span>
          ) : null}
        </div>
        <Link
          to={buildPatientChartPath(patientId, 'demographics')}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-primary inline-flex items-center gap-1 text-xs text-gray-500 underline-offset-2 hover:underline"
        >
          Open chart
          <IconExternalLink size={12} />
        </Link>
      </div>

      {loading ? (
        <p role="status" className="flex items-center gap-2 text-sm text-[#64748B]">
          <Loader size="xs" />
          Loading verification status…
        </p>
      ) : null}

      {attempt ? <PersonaAttemptSummary attempt={attempt} /> : null}

      {attempt?.governmentIdResult ? (
        <PersonaIdImage
          patientId={patientId}
          attemptId={attempt.id}
          verified={identity?.status === 'verified_auto' && identity.method === 'persona'}
        />
      ) : null}

      {identity?.verified ? (
        <p className="text-sm text-green-700">
          Identity verified — this task completes on its own.
        </p>
      ) : canVerify ? (
        <div>
          <Button
            size="xs"
            color="green"
            leftSection={<IconShieldCheck size={14} />}
            loading={verifying}
            onClick={confirmVerify}
          >
            Mark identity verified
          </Button>
          <p className="mt-1 text-xs text-[#64748B]">
            Verification method, reviewer and time are recorded automatically.
          </p>
        </div>
      ) : null}
    </section>
  )
}
